import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExSideDrawer,
  ButtonType,
  ButtonSize,
  ExButton,
  ButtonFlavor,
  ExLoader,
  LoaderSize,
  LoaderVariant,
  ExTooltip,
  TooltipPosition,
  TooltipAlignment,
  ExAlertBanner,
  AlertBannerType,
  AlertBannerVariant,
  // ExBadge,
  // BadgeColor,
  // BadgeSize,
} from '@boomi/exosphere';
import modelsApiService, {
  type ModelConfigDetailsResponse,
} from '@services/agent-foundation/agent-foundation-models-api-service';
import './ModelConfigDetailsDrawer.scss';
import { providerLogo, ModelsListDrawer, ModelAgentsDrawer } from '@components/ModelsManagement';
import { formatDate } from '@components/ListViewUtils/ListViewUtils';

interface ModelConfigDetailsDrawerProps {
  open: boolean;
  configId: string;
  onClose: () => void;
  onEdit?: () => void;
  config?: ModelConfigDetailsResponse | null;
}

function ModelConfigDetailsDrawer({
  open,
  configId,
  onClose,
  onEdit,
  config: initialConfig,
}: ModelConfigDetailsDrawerProps) {
  const [config, setConfig] = useState<ModelConfigDetailsResponse | null>(initialConfig || null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAgentModel, setSelectedAgentModel] = useState<any>(null);

  useEffect(() => {
    // If config is already provided, use it directly
    if (initialConfig) {
      setConfig(initialConfig);
      return;
    }

    if (!configId) return;

    const fetchConfigDetails = async () => {
      setIsLoading(true);
      try {
        const details = await modelsApiService.getModelConfigDetails(configId);
        setConfig(details?.data || null);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load config details:', error);
        setConfig(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigDetails();
  }, [configId, initialConfig]);
  const [t] = useTranslation('common');
  const logo = config ? providerLogo(config.provider) : null;
  const environments = config?.environments || [];
  const envNames = environments.map((env: any) => env.environment_name);
  const firstOne = envNames.slice(0, 1);
  const remaining = envNames.length - 1;
  const envDisplayText = remaining > 0
    ? `${firstOne.join(', ')} (+${remaining})`
    : firstOne.join(', ');
  const hasDeprecatedModel = config?.models?.some(
    (option: any) => option.deployment_count > 0 && option.status?.toLowerCase() === 'deprecated',
  );
  const hasRetiredModel = config?.models?.some(
    (option: any) => option.deployment_count > 0 && option.status?.toLowerCase() === 'retired',
  );
  function mapProviderFromResponse(provider: any): string {
    switch (provider) {
      case 'anthropic':
        return t('models_provider_anthropic');
      case 'gemini':
        return t('models_provider_gemini');
      case 'google':
        return t('models_provider_google');
      case 'openai':
        return t('models_provider_openai');
      default:
        return 'None';
    }
  }
  return (
    <div className="model-config-details-drawer">
      <ExSideDrawer
        open={open}
        panelTitle={config?.name}
        onClose={onClose}
        onCancel={onClose}
        data-testid="model-config-details-side-drawer"
        className="model-config-details-drawer__content"
        preventStacking
      >
        {isLoading || !config ? (
          <div className="model-config-details-drawer__loader">
            <ExLoader
              size={LoaderSize.MEDIUM}
              variant={LoaderVariant.SPINNER}
            />
          </div>
        ) : (
          <div className="model-config-details-drawer__body">
            <div className="model-config-details-drawer__section">
              {/* Provider Section */}
              <div className="model-config-details-drawer__info-grid">
                <div className="model-config-details-drawer__info-item">
                  <span className="model-config-details-drawer__label">{t('model_provider')}</span>
                  <div className="model-config-details-drawer__provider-details">
                    <div className="model-config-details-drawer__provider">
                      {logo && (
                        <img
                          src={logo.src}
                          alt={logo.alt}
                          className="model-config-details-drawer__provider-icon"
                        />
                      )}
                    </div>
                    <span className="model-config-details-drawer__provider-name">{mapProviderFromResponse(config.provider)}</span>
                  </div>
                </div>
                <div className="model-config-details-drawer__info-item">
                  <span className="model-config-details-drawer__label">{t('environment')}</span>
                  {envNames.length > 0 ? (
                    <ExTooltip
                      tooltipDelay={0}
                      position={TooltipPosition.TOP}
                      alignment={TooltipAlignment.MIDDLE}
                    >
                      <div style={{
                        overflowWrap: 'anywhere',
                        width: '100%',
                        overflow: 'hidden',
                        whiteSpace: 'normal',
                      }}
                      >
                        {envNames.join(', ')}
                      </div>
                      <p
                        slot="anchor"
                        className="model-config-details-drawer__value"
                        style={{ cursor: 'default' }}
                      >
                        {envDisplayText}
                      </p>
                    </ExTooltip>
                  ) : (
                    <p className="model-config-details-drawer__value">-</p>
                  )}
                </div>
              </div>
              {/* Info Grid */}
              <div className="model-config-details-drawer__info-grid">
                <div className="model-config-details-drawer__info-item">
                  <span className="model-config-details-drawer__label">{t('models_table_column_last_updated')}</span>
                  <p className="model-config-details-drawer__value">{formatDate(config.last_updated_on)}</p>
                </div>
                <div className="model-config-details-drawer__info-item">
                  <span className="model-config-details-drawer__label">{t('ai_created_by')}</span>
                  <p className="model-config-details-drawer__value">{config.created_by}</p>
                </div>
              </div>
              <div className="model-config-details-drawer__info-grid-single">
                <div className="model-config-details-drawer__info-item">
                  <span className="model-config-details-drawer__label">{t('id')}</span>
                  <p className="model-config-details-drawer__value">{config.id}</p>
                </div>
              </div>
            </div>
            {/* Models Section */}
            {hasDeprecatedModel && (
              <ExAlertBanner
                type={AlertBannerType.WARNING}
                open
                variant={AlertBannerVariant.SUBTLE}
                className="register-model-drawer__warning-banner"
                data-testid="model-config-deprecated-model-warning"
              >
                {t('models_deprecated_model_banner')}
              </ExAlertBanner>
            )}
            {hasRetiredModel && (
              <ExAlertBanner
                type={AlertBannerType.ERROR}
                open
                variant={AlertBannerVariant.SUBTLE}
                className="register-model-drawer__warning-banner"
                data-testid="model-config-deprecated-model-warning"
              >
                {t('models_retired_model_banner')}
              </ExAlertBanner>
            )}
            <div className="model-config-details-drawer__models-section">
              <ModelsListDrawer
                open
                configName={config.name}
                configId={config.id}
                provider={config.provider}
                inline
                initialModels={config?.models}
                onClose={() => {}}
                onShowAgents={(model) => {
                  setSelectedAgentModel(model);
                }}
              />
            </div>
          </div>
        )}
        <div slot="footer" className="model-config-details-drawer__footer">
          <ExButton
            type={ButtonType.SECONDARY}
            size={ButtonSize.DEFAULT}
            flavor={ButtonFlavor.BRANDED}
            onClick={onClose}
            data-testid="model-config-details-close-btn"
          >
            Close
          </ExButton>
          {onEdit && (
            <ExButton
              type={ButtonType.PRIMARY}
              size={ButtonSize.DEFAULT}
              flavor={ButtonFlavor.BASE}
              onClick={onEdit}
              data-testid="model-config-details-edit-btn"
            >
              Edit
            </ExButton>
          )}
        </div>
      </ExSideDrawer>
      {selectedAgentModel && (
        <ModelAgentsDrawer
          open={!!selectedAgentModel}
          modelId={selectedAgentModel.model_id || ''}
          modelName={selectedAgentModel.display_name || ''}
          modelConfigId={configId}
          provider={selectedAgentModel.provider || 'Custom'}
          navigation
          onClose={() => {
            setSelectedAgentModel(null);
          }}
          onParentClose={onClose}
        />
      )}
    </div>
  );
}

ModelConfigDetailsDrawer.defaultProps = {
  onEdit: undefined,
  config: undefined,
};

export default ModelConfigDetailsDrawer;
